/**
 * Lender-tier score display helpers (WP-8 Slice 8C).
 *
 * Centralizes the null-vs-zero invariant for the Lenders App so that missing,
 * insufficient, held, or unavailable scores never render as a literal `0`.
 *
 * Producer contract: api-verascore-platform Slice 8A emits additive optional
 * fields on /lender/members and /lender/members/:id/history (see
 * LenderMember.score_state, MemberHistoryPoint.score_state /
 * .score_nullable, LenderWindowState on rolling-average windows, etc.).
 * Lender-tier consumers MUST prefer those companions over legacy numeric
 * fields and MUST surface lender-safe copy for non-renderable states.
 *
 * Governance refs:
 *  - ADR-WP8-001 §5/§6/§11/§13 (taxonomy, friendly copy, internal suppression)
 *  - 13-WORK-LOGS/Audit/2026-06-03/wp-8-ui-display/
 *      UI-DISPLAY-DATA-CONTRACT-v0.1.md §10/§11/§13/§18/§21
 *  - LENDERS-APP-MEMBER-LIST-DISPLAY-SPEC-v0.1.md
 *  - LENDERS-APP-MEMBER-DETAIL-DISPLAY-SPEC-v0.1.md
 *  - LENDERS-APP-MEMBER-HISTORY-DISPLAY-SPEC-v0.1.md
 */

import type {
  LenderHistoryScoreState,
  LenderWindowState,
  LenderFactorState,
  MemberHistoryPoint,
} from '../services/lenderApiService';

/** Display verdict returned by selectors. */
export interface ScoreDisplay {
  /** Numeric value to render, or null when the UI MUST NOT render a number. */
  value: number | null;
  /** Lender-safe label to render when `value` is null (or as a tooltip). */
  label: string;
  /** True when the value is a placeholder for missing/held/unavailable data. */
  isPlaceholder: boolean;
  /** Originating taxonomy state (best-effort; absent on legacy fallback). */
  state?: LenderHistoryScoreState | LenderWindowState | LenderFactorState;
}

/**
 * Lender-safe friendly copy. ADR-WP8-001 §6 + §11. These strings are the only
 * copy that may surface to lenders for non-renderable states. Raw taxonomy
 * enum values, internal hold reasons, and exclusion-reason codes MUST NOT
 * be passed through to lender-facing UI.
 */
export const FRIENDLY_COPY = {
  no_official_score: 'No official score',
  score_on_hold: 'Score temporarily on hold',
  score_unavailable: 'Score unavailable',
  insufficient_history: 'Not enough history',
  policy_excluded: 'Score not available',
  under_review: 'Score under review',
  no_history: 'No score history available',
  history_hidden: 'Score history not available',
} as const;

/**
 * WP-8 Slice 8D — lender-safe modal-level copy. These strings are surfaced
 * inside the Click-to-Explain modal body when no evidence is renderable
 * (either no `algo_run_evidence` row, no `/history.inputs` fallback, or the
 * row's state is non-renderable). They MUST remain free of internal taxonomy
 * leakage per ADR-WP8-001 §6/§11/§13.
 */
export const MODAL_UNAVAILABLE_COPY = {
  detail_not_available_for_month: 'Detail not available for this month.',
  detail_not_available_for_metric: 'Detail not available for this metric yet.',
  score_on_hold: FRIENDLY_COPY.score_on_hold,
  score_unavailable: FRIENDLY_COPY.score_unavailable,
  insufficient_history: FRIENDLY_COPY.insufficient_history,
  policy_excluded: FRIENDLY_COPY.policy_excluded,
  under_review: FRIENDLY_COPY.under_review,
} as const;

/** History-row states for which the UI MUST NOT render the numeric score. */
const NON_RENDERABLE_HISTORY_STATES: ReadonlySet<LenderHistoryScoreState> = new Set([
  'held_incomplete_data',
  'unavailable',
  'error',
  'insufficient_history',
  'legacy_unknown',
]);

/** Window states that indicate the rolling-average value is not renderable. */
const NON_RENDERABLE_WINDOW_STATES: ReadonlySet<LenderWindowState> = new Set([
  'insufficient_data',
  'under_review',
  'held_incomplete_data',
  'policy_excluded',
]);

/** Factor states that mean the numeric value is NOT a measured zero. */
const NON_RENDERABLE_FACTOR_STATES: ReadonlySet<LenderFactorState> = new Set([
  'not_applicable',
  'insufficient_data',
  'unavailable',
  'error',
]);

function labelForHistoryState(state?: LenderHistoryScoreState): string {
  switch (state) {
    case 'held_incomplete_data':
      return FRIENDLY_COPY.score_on_hold;
    case 'unavailable':
    case 'error':
      return FRIENDLY_COPY.score_unavailable;
    case 'insufficient_history':
      return FRIENDLY_COPY.insufficient_history;
    case 'legacy_unknown':
    default:
      return FRIENDLY_COPY.no_official_score;
  }
}

function labelForWindowState(state?: LenderWindowState): string {
  switch (state) {
    case 'held_incomplete_data':
      return FRIENDLY_COPY.score_on_hold;
    case 'under_review':
      return FRIENDLY_COPY.under_review;
    case 'insufficient_data':
      return FRIENDLY_COPY.insufficient_history;
    case 'policy_excluded':
      return FRIENDLY_COPY.policy_excluded;
    default:
      return FRIENDLY_COPY.no_official_score;
  }
}

/**
 * Select the current-score display for a lender member. Prefers the additive
 * `verascore_nullable` companion when present; otherwise falls back to the
 * legacy `verascore` field. Returns a placeholder (`value: null`) when
 * `score_state` indicates a non-renderable state, regardless of any legacy
 * numeric coercion upstream.
 */
export function selectCurrentScoreDisplay(input: {
  verascore?: number | null;
  verascore_nullable?: number | null;
  score_state?: LenderHistoryScoreState;
}): ScoreDisplay {
  const { score_state } = input;
  if (score_state && NON_RENDERABLE_HISTORY_STATES.has(score_state)) {
    return {
      value: null,
      label: labelForHistoryState(score_state),
      isPlaceholder: true,
      state: score_state,
    };
  }
  const raw =
    input.verascore_nullable !== undefined
      ? input.verascore_nullable
      : input.verascore ?? null;
  if (raw === null || raw === undefined) {
    return {
      value: null,
      label: FRIENDLY_COPY.no_official_score,
      isPlaceholder: true,
      state: score_state,
    };
  }
  return { value: raw, label: '', isPlaceholder: false, state: score_state };
}

/**
 * Select the rolling-average window display (6/12/24 month). The producer
 * already returns `value: null` when the window is not eligible, but the
 * additive `*_score_state` enum allows the FE to surface a meaningful label
 * (e.g., "Score temporarily on hold") rather than a generic "—".
 */
export function selectRollingWindowDisplay(
  value: number | null | undefined,
  windowState?: LenderWindowState,
): ScoreDisplay {
  if (windowState && NON_RENDERABLE_WINDOW_STATES.has(windowState)) {
    return {
      value: null,
      label: labelForWindowState(windowState),
      isPlaceholder: true,
      state: windowState,
    };
  }
  if (value === null || value === undefined) {
    return {
      value: null,
      label: FRIENDLY_COPY.insufficient_history,
      isPlaceholder: true,
      state: windowState,
    };
  }
  return { value, label: '', isPlaceholder: false, state: windowState };
}

/**
 * Select a per-factor display. `measured_zero` is the only non-`measured`
 * state for which a 0 value is legitimately renderable.
 */
export function selectFactorDisplay(
  value: number | null | undefined,
  factorState?: LenderFactorState,
): ScoreDisplay {
  if (factorState && NON_RENDERABLE_FACTOR_STATES.has(factorState)) {
    return {
      value: null,
      label: FRIENDLY_COPY.score_unavailable,
      isPlaceholder: true,
      state: factorState,
    };
  }
  if (value === null || value === undefined) {
    return {
      value: null,
      label: FRIENDLY_COPY.score_unavailable,
      isPlaceholder: true,
      state: factorState,
    };
  }
  return { value, label: '', isPlaceholder: false, state: factorState };
}

/**
 * Filter a member-history series to only the rows that are safe to render as
 * a numeric score on lender surfaces (charts, timelines, tables). Drops rows
 * whose `score_state` is non-renderable AND drops rows whose additive
 * `score_nullable` companion is explicitly null. Rows without WP-8 state
 * metadata (pre-Slice-8A producers) are preserved for back-compat.
 */
export function filterRenderableHistoryPoints<T extends MemberHistoryPoint>(
  series: readonly T[] | null | undefined,
): T[] {
  if (!series || series.length === 0) return [];
  return series.filter((point) => {
    if (point.score_state && NON_RENDERABLE_HISTORY_STATES.has(point.score_state)) {
      return false;
    }
    if (point.score_nullable === null) return false;
    return true;
  });
}

/**
 * Resolve a lender-safe empty-state label for a history response with no
 * renderable points. Suppresses the `hidden_by_policy` discriminator from
 * lender surfaces per ADR-WP8-001 §8 / Contract §20; both internal reasons
 * collapse to a neutral copy.
 */
export function selectHistoryEmptyLabel(
  emptyReason?: 'no_history' | 'hidden_by_policy',
): string {
  if (emptyReason === 'hidden_by_policy') return FRIENDLY_COPY.history_hidden;
  return FRIENDLY_COPY.no_history;
}

/**
 * WP-8 Slice 8D — modal-level empty state selector. Maps a non-renderable
 * state (or absence of any source) to the lender-safe copy that the modal
 * body MUST show in place of a generic "No evidence available" error and
 * in place of a literal `0`. Returns null when the state IS renderable
 * (caller should render the value, not an empty state).
 */
export function selectModalEmptyDisplay(
  state?: LenderHistoryScoreState | LenderWindowState | LenderFactorState,
): string | null {
  if (!state) return MODAL_UNAVAILABLE_COPY.detail_not_available_for_month;
  switch (state) {
    case 'held_incomplete_data':
      return MODAL_UNAVAILABLE_COPY.score_on_hold;
    case 'insufficient_data':
    case 'insufficient_history':
      return MODAL_UNAVAILABLE_COPY.insufficient_history;
    case 'under_review':
      return MODAL_UNAVAILABLE_COPY.under_review;
    case 'policy_excluded':
      return MODAL_UNAVAILABLE_COPY.policy_excluded;
    case 'unavailable':
    case 'error':
      return MODAL_UNAVAILABLE_COPY.score_unavailable;
    case 'legacy_unknown':
    case 'dedup_pending_evidence':
      return MODAL_UNAVAILABLE_COPY.detail_not_available_for_month;
    // Renderable states — caller renders the value, no empty state required.
    case 'official_current':
    case 'official_historical':
    case 'dedup_adjusted':
    case 'ok':
    case 'measured':
    case 'measured_zero':
      return null;
    case 'not_applicable':
      return MODAL_UNAVAILABLE_COPY.score_unavailable;
    default:
      return MODAL_UNAVAILABLE_COPY.detail_not_available_for_month;
  }
}

/**
 * WP-8 Slice 8D — modal value display posture. Preserves a measured numeric
 * value (including a true `0` and `0.01`) and routes `null` / `undefined`
 * through the lender-safe modal copy. Never coerces a missing value to `0`.
 */
export interface ModalValueDisplay {
  value: number | null;
  label: string;
  isPlaceholder: boolean;
}

export function selectModalValueDisplay(
  value: number | null | undefined,
  state?: LenderHistoryScoreState | LenderWindowState | LenderFactorState,
): ModalValueDisplay {
  const stateCopy = selectModalEmptyDisplay(state);
  if (stateCopy !== null) {
    return { value: null, label: stateCopy, isPlaceholder: true };
  }
  if (value === null || value === undefined || Number.isNaN(value)) {
    return {
      value: null,
      label: MODAL_UNAVAILABLE_COPY.detail_not_available_for_month,
      isPlaceholder: true,
    };
  }
  return { value, label: '', isPlaceholder: false };
}

/**
 * WP-8 Slice 8D — allowlisted set of `algo_run_evidence` `entity_name`
 * values for which the Lenders App modal MAY synthesize a display-only
 * evidence body from `/lender/members/{id}/history` `inputs` when no
 * `algo_run_evidence` row exists for the selected month. Each key maps
 * to a single field on `MemberHistoryInputs`. ADR-WP8-001 §6 + §11 —
 * lender-safe; no internal-only metric is allowlisted.
 */
export const HISTORY_INPUT_FALLBACK_KEYS = {
  gross_income_6m: 'yearly_gross_income',
  net_savings_1m: 'net_savings',
  money_leftover_1m: 'money_leftover',
  money_out_1m: 'monthly_expenses',
  total_debt: 'outstanding_debt',
  dti_ratio: 'dti_ratio',
  cash_on_hand: 'liquid_reserves',
  monthly_debt_payments: 'minimum_debt_payments',
} as const;

export type HistoryFallbackMetricKey = keyof typeof HISTORY_INPUT_FALLBACK_KEYS;

export function isHistoryFallbackMetricKey(
  metricKey: string,
): metricKey is HistoryFallbackMetricKey {
  return Object.prototype.hasOwnProperty.call(HISTORY_INPUT_FALLBACK_KEYS, metricKey);
}

/**
 * WP-8 Slice 8D — eligibility predicate for using a `MemberHistoryPoint`
 * as the source of a modal-body synthesized evidence row. Excludes legacy,
 * held, error, unavailable, and policy-excluded rows by default, and
 * defends against `included_in_rolling_average === false`. Pre-WP-8 rows
 * (no `score_state` metadata) are NOT eligible for synthesis to avoid
 * surfacing data whose provenance the producer has not yet attested.
 */
export function isEligibleFallbackHistoryRow(
  row: Pick<MemberHistoryPoint, 'score_state' | 'included_in_rolling_average'> & {
    display_state?: string | null;
  } | null | undefined,
): boolean {
  if (!row) return false;
  if (row.included_in_rolling_average === false) return false;
  if (typeof row.display_state === 'string' && row.display_state.toLowerCase() === 'legacy') {
    return false;
  }
  const eligibleStates: ReadonlySet<LenderHistoryScoreState> = new Set([
    'official_current',
    'official_historical',
    'dedup_adjusted',
  ]);
  if (!row.score_state) return false;
  return eligibleStates.has(row.score_state);
}

